import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, useTheme,
} from '@mui/material';
import {
  Warehouse as WarehouseIcon,
  PlayArrow as RunIcon,
  KeyboardArrowRight as CollapsedIcon,
  KeyboardArrowDown as ExpandedIcon,
} from '@mui/icons-material';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import inventoryReportService from '../../../../services/inventoryReportService';
import type { WarehouseConsumptionReport as ReportData } from '../../../../services/inventoryReportService';
import warehouseService from '../../../../services/warehouseService';
import type { Warehouse } from '../../../../services/warehouseService';
import DateRangeFilter from '../../../../components/venue/DateRangeFilter';
import PageHeader from '../../../../components/venue/PageHeader';
import FilterBar from '../../../../components/venue/FilterBar';
import EmptyState from '../../../../components/venue/EmptyState';
import { formatQty } from '../../../../utils/quantityFormat';
import { fromMinorUnitsOrNull } from '../../../../utils/money';

/**
 * REPORTING.R6 — who consumed a warehouse's stock.
 *
 * COGS attribution deliberately moves a cross-venue sale OFF the owner's report
 * and on to the seller's: the asset was the company's, the cost of sales is the
 * venue that poured it. Right for cost, and it left the venue administering a
 * shared cellar with no way to see who drank their stock.
 *
 * So this page is the reciprocal view, and it reads by VENUE first and item
 * second — the opposite of every other report here, because the question is
 * "who took it", not "what moved".
 */

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const dayStart = (day: string) => new Date(`${day}T00:00:00.000Z`).toISOString();
const dayEnd = (day: string) => new Date(`${day}T23:59:59.999Z`).toISOString();

const WarehouseConsumptionReport: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();
  const { canViewStockCosts } = useAuth();
  const showCost = canViewStockCosts();

  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 864e5);

  const [fromDay, setFromDay] = useState(isoDay(monthAgo));
  const [toDay, setToDay] = useState(isoDay(today));
  const [warehouseId, setWarehouseId] = useState('');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!venueId) return;
    warehouseService.list(venueId)
      .then(res => {
        setWarehouses(res.warehouses);
        // A shared cellar is what this report is FOR, so default to the first
        // one rather than to whichever warehouse happens to sort first.
        const shared = res.warehouses.find(w => w.homeVenueId === null);
        setWarehouseId(String((shared ?? res.warehouses[0])?._id ?? ''));
      })
      .catch(() => setError('Could not load warehouses.'));
  }, [venueId]);

  const runReport = useCallback(async () => {
    if (!venueId || !warehouseId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryReportService.getWarehouseConsumption(venueId, {
        warehouseId, from: dayStart(fromDay), to: dayEnd(toDay),
      });
      setReport(result);
      setExpanded(new Set());
    } catch (err: any) {
      setReport(null);
      setExpanded(new Set());
      setError(err?.response?.data?.error?.message ?? 'Failed to build the report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [venueId, warehouseId, fromDay, toDay]);

  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const money = (v: number | null | undefined) => {
    const n = fromMinorUnitsOrNull(v ?? null);
    return n == null ? '—' : n.toFixed(2);
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        icon={<WarehouseIcon />}
        title="Warehouse Consumption by Venue"
        subtitle="Who consumed this warehouse's stock, and how much of it."
      />

      <FilterBar>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Warehouse</InputLabel>
          <Select
            label="Warehouse"
            value={warehouseId}
            onChange={e => setWarehouseId(e.target.value)}
          >
            {warehouses.map(w => (
              <MenuItem key={w._id} value={w._id}>
                {w.name}{w.homeVenueId === null ? ' (shared)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DateRangeFilter
          from={fromDay}
          to={toDay}
          onFromChange={setFromDay}
          onToChange={setToDay}
          disabled={loading}
        />

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
          onClick={runReport}
          disabled={loading || !warehouseId || !fromDay || !toDay}
        >
          Run
        </Button>
      </FilterBar>

      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && report && report.byVenue.length === 0 && (
        <EmptyState
          icon={WarehouseIcon}
          message="No consumption in this period"
          hint="Nothing was sold or wasted from this warehouse between those dates."
        />
      )}

      {!loading && report && report.byVenue.length > 0 && (
        <>
          {/*
            NOT a footnote. A reader comparing this to the Movements report will
            find less depletion here, and the difference has to be visible in the
            page rather than discovered. Counts, adjustments and production do
            not record which venue performed them, so they cannot be attributed.
          */}
          {report.meta.excludedMovementCount > 0 && (
            <Alert severity="info">
              {report.meta.excludedMovementCount} other depletion movement
              {report.meta.excludedMovementCount === 1 ? '' : 's'} in this period
              {report.meta.excludedMovementCount === 1 ? ' is' : ' are'} not shown.
              Stock counts, manual adjustments and production do not record which
              venue performed them, so they cannot be attributed to one.
            </Alert>
          )}

          <Paper variant="outlined">
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography fontWeight={700}>{report.meta.warehouseName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatQty(report.totals.quantity)} consumed
                {showCost && report.totals.cost != null ? ` · ${money(report.totals.cost)}` : ''}
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Venue</TableCell>
                    <TableCell align="right" sx={{ width: 140 }}>Quantity</TableCell>
                    {showCost && <TableCell align="right" sx={{ width: 140 }}>Cost</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.byVenue.map(group => {
                    const isOpen = expanded.has(group.venueId);
                    return (
                      <React.Fragment key={group.venueId}>
                        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(group.venueId)}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              {isOpen ? <ExpandedIcon fontSize="small" /> : <CollapsedIcon fontSize="small" />}
                              <Typography variant="body2" fontWeight={600}>
                                {group.venueName ?? `(venue ${group.venueId.slice(-6)})`}
                              </Typography>
                              {/*
                                The venue that administers the cellar is not a
                                guest, and reads differently: its consumption is
                                its own stock, not someone else's.
                              */}
                              {group.isHomeVenue && (
                                <Chip size="small" variant="outlined" label="Administers this warehouse" />
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {group.items.length} item{group.items.length === 1 ? '' : 's'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatQty(group.quantity)}
                          </TableCell>
                          {showCost && (
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{money(group.cost)}</TableCell>
                          )}
                        </TableRow>

                        {isOpen && group.items.map(item => (
                          <TableRow
                            key={`${group.venueId}:${item.inventoryItemId}`}
                            sx={{ bgcolor: theme.palette.action.hover }}
                          >
                            <TableCell sx={{ pl: 5, borderBottom: 'none', py: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {item.itemName ?? `(deleted item ${item.inventoryItemId.slice(-6)})`}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: 'none', py: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatQty(item.quantity)}
                              </Typography>
                            </TableCell>
                            {showCost && (
                              <TableCell align="right" sx={{ borderBottom: 'none', py: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {money(item.cost)}
                                </Typography>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default WarehouseConsumptionReport;
